/**
 * @fileoverview ContactInformation component for managing company contact details.
 * This component handles phone numbers (office, extension, secondary), email addresses,
 * and website URLs with country code selection and visibility toggle controls.
 */

import styles from "./ContactInformation.module.css";
import {
  MainEmailField,
  WebsiteField,
} from "./ContactInformationEmailWebsiteFields";
import {
  ExtensionPhoneField,
  OfficePhoneField,
  SecondaryPhoneField,
} from "./ContactInformationPhoneFields";
import { useContactInformationSection } from "./useContactInformationSection";

/**
 * ContactInformation component for displaying and editing company contact details.
 * Manages phone numbers with country codes, email addresses, and website URLs
 * with visibility toggles for public profile display.
 *
 * @param {Object} props - Component props
 * @param {Object} props.company - Company data object containing contact information fields
 * @param {Function} props.onFieldChange - Callback function triggered when a field value changes
 * @param {Function} props.onValidationChange - Callback function triggered when validation state changes
 * @param {number} [props.selectedProfile=0] - Currently selected company profile index for multi-profile support
 * @returns {JSX.Element} The rendered ContactInformation form section
 */
const ContactInformation = ({
  company,
  onFieldChange,
  onValidationChange,
  selectedProfile = 0,
}) => {
  const section = useContactInformationSection({
    company,
    onFieldChange,
    onValidationChange,
    selectedProfile,
  });

  return (
    <div className={styles.sectionContent}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>
          {section.t("components.contactInformation.title")}
        </span>
        <div className={styles.sectionDivider}></div>
      </div>
      <div className={styles.inputRow}>
        <OfficePhoneField section={section} />
        <ExtensionPhoneField section={section} />
        <SecondaryPhoneField section={section} />
        <MainEmailField section={section} />
      </div>
      <WebsiteField section={section} />
    </div>
  );
};

export default ContactInformation;
